
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ProjectList } from "@/components/projects/ProjectList";
import { ProjectKanbanBoard } from "@/components/projects/ProjectKanbanBoard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ProjectForm } from "@/components/projects/ProjectForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Table as TableIcon, Columns } from "lucide-react";

const Projects = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentOrganization, isLoading: orgLoading, organizations } = useOrganization();
  const [activeView, setActiveView] = useState<"table" | "kanban">("table");
  const [complexityFilter, setComplexityFilter] = useState<"all" | "low" | "medium" | "high">("all");

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to access this page",
          variant: "destructive",
        });
        navigate("/auth");
      }
    };
    checkUser();
  }, [navigate, toast]);

  // Debug logging for organization state
  useEffect(() => {
    console.log('📊 Projects page: Organization state', {
      currentOrganization,
      orgLoading,
      organizationsCount: organizations.length,
      hasOrganizations: organizations.length > 0
    });
  }, [currentOrganization, orgLoading, organizations]);

  // Set up real-time subscription to organization-level changes
  useEffect(() => {
    if (!currentOrganization) return;
    
    const channel = supabase
      .channel('org-projects-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'projects',
          filter: `organization_id=eq.${currentOrganization.id}`
        },
        () => {
          // When a new project is added, invalidate projects query
          queryClient.invalidateQueries({ 
            queryKey: ["projects", currentOrganization.id] 
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentOrganization, queryClient]);

  if (orgLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Projects</h1>
            {currentOrganization && (
              <p className="text-muted-foreground mt-1">
                {currentOrganization.name}
              </p>
            )}
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Project</DialogTitle>
              </DialogHeader>
              <ProjectForm onSuccess={() => {
                toast({
                  title: "Success",
                  description: "Project created successfully"
                });
              }} />
            </DialogContent>
          </Dialog>
        </div>

        <Tabs value={activeView} onValueChange={(v) => setActiveView(v as "table" | "kanban")} className="w-full">
          <div className="flex justify-between items-center mb-4">
            <TabsList>
              <TabsTrigger value="table" className="gap-2">
                <TableIcon className="h-4 w-4" />
                Table View
              </TabsTrigger>
              <TabsTrigger value="kanban" className="gap-2">
                <Columns className="h-4 w-4" />
                Kanban View
              </TabsTrigger>
            </TabsList>
            
            {activeView === "kanban" && (
              <Select value={complexityFilter} onValueChange={(v) => setComplexityFilter(v as "all" | "low" | "medium" | "high")}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by complexity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Complexities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          <TabsContent value="table" className="mt-0">
            <ProjectList />
          </TabsContent>

          <TabsContent value="kanban" className="mt-0">
            <ProjectKanbanBoard complexityFilter={complexityFilter} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Projects;
